import { useMemo } from "react";

import type { Draft, Exercise, SessionDraft } from "@jackedlogs/shared";

import { slotDraftToEntry } from "../../Logger/slotDraftHydration";
import { rowIdOf, type DraftEntry } from "../../Logger/state/loggerDraftTypes";
import type { UnitsPreference } from "@jackedlogs/shared";
import type { WeightUnit } from "../../Logger/state/loggerDraftTypes";

// PE-27 fetta 2 — the roster of the program editor lists THE DAY, not the
// week.
//
// ⛔ It is a SECOND list, deliberately, and it must never be handed to a
// dispatch. `usePrescribeMount`'s `draft.entries` is "the slots active at the
// selected week", and every write in `prescribeDispatch.ts` addresses a slot
// by its position in exactly that list (`activeSlotIdxsAt`). Widening it to
// carry the unplaced slots would silently re-aim every reps edit, ± set and
// remove at the wrong exercise. So the strip gets its own list, and the card
// keeps the one it can write through.
//
// What the dimmed rows are for: the roster stays STILL while the card flips
// weeks (the swipe of fetta 1 is the progression). An exercise that is not
// placed in the week on screen has to keep its chip, because a chip that
// vanishes reads as "removed" — the athlete's word for it, from the day the
// card fell to a blank empty-state instead.

export type PrescribeRoster = {
  /** Every slot of the day, in slot order, whether or not it is placed in the
   *  week on screen. */
  entries: DraftEntry[];
  /** The subset that is NOT placed there, keyed by `rowIdOf` — the same
   *  function RosterStrip keys its chips with. */
  inactiveIds: Set<string>;
};

/** An unplaced slot has no cells to read at THIS week, so its row is built at
 *  the first week it is actually placed in. Everything the chip shows from it
 *  (name, group, kind) is week-independent; the counts are not, which is why
 *  the strip prints no count on a dimmed chip. */
const firstActiveWeek = (slot: SessionDraft["slots"][number]): number => {
  const at = slot.inactiveWeeks.findIndex((off) => !off);
  return at === -1 ? 0 : at;
};

export const buildPrescribeRoster = (
  session: SessionDraft | undefined,
  weekIdx: number,
  opts: {
    catalog: Exercise[] | null;
    getExerciseNameBySlug: (slug: string, fallback?: string) => string;
    cellUnit?: UnitsPreference;
    displayUnit?: WeightUnit;
  },
): PrescribeRoster => {
  const entries: DraftEntry[] = [];
  const inactiveIds = new Set<string>();
  for (const slot of session?.slots ?? []) {
    const inactive = slot.inactiveWeeks[weekIdx] === true;
    const exerciseRow = opts.catalog?.find((c) => c.slug === slot.exerciseId);
    const entry = slotDraftToEntry(slot, inactive ? firstActiveWeek(slot) : weekIdx, {
      exercise: exerciseRow
        ? {
            name: opts.getExerciseNameBySlug(exerciseRow.slug, slot.exerciseName),
            isBodyweight: exerciseRow.isBodyweight,
            movementPattern: exerciseRow.movementPattern,
            isTimeBased: exerciseRow.isTimeBased,
            unilateral: exerciseRow.unilateral,
          }
        : undefined,
      ...(opts.cellUnit ? { cellUnit: opts.cellUnit } : {}),
      ...(opts.displayUnit ? { displayUnit: opts.displayUnit } : {}),
    });
    // Null means the slot is off in EVERY week — nothing to show and nothing
    // to place, so it is not a dimmed chip, it is not a chip.
    if (!entry) continue;
    entries.push(entry);
    // ⛔ `rowIdOf`, never a hand-rolled `entryId ?? exerciseId`. That is the
    // exact function RosterStrip keys its chips with, and the two have to be
    // the SAME function or a set built here would miss the chip it means: the
    // fallback branch keys on the slug, and two rows of the same exercise
    // would collapse onto one id (LG-39, the bug that made rowIdOf exist).
    if (inactive) inactiveIds.add(rowIdOf(entry));
  }
  return { entries, inactiveIds };
};

/** The memoised form the mount uses. Lives here rather than inline in
 *  `usePrescribeMount`, which has no room left under the 500-line cap and no
 *  reason to own this list twice over. */
export const usePrescribeRoster = (
  programDraft: Draft,
  selectedDay: number,
  weekIdx: number,
  opts: {
    catalog: Exercise[] | null;
    getExerciseNameBySlug: (slug: string, fallback?: string) => string;
    cellUnit?: UnitsPreference;
    displayUnit?: WeightUnit;
  },
): PrescribeRoster => {
  const { catalog, getExerciseNameBySlug, cellUnit, displayUnit } = opts;
  return useMemo(
    () =>
      buildPrescribeRoster(programDraft.sessions[selectedDay], weekIdx, {
        catalog,
        getExerciseNameBySlug,
        cellUnit,
        displayUnit,
      }),
    [
      programDraft,
      selectedDay,
      weekIdx,
      catalog,
      getExerciseNameBySlug,
      cellUnit,
      displayUnit,
    ],
  );
};
